import { buildSidusisPlaceUrl } from '../layers/SidusisLayer';
import { geoportalMapviewUrl, qstr } from '../utils/url';
import { guideLine } from '../../shared/guide-link.js';

const GEOPORTAL_PLACE_LABEL = 'Otwórz to miejsce w Geoportalu';
const geoportalPlace = (latlng) => ({ label: GEOPORTAL_PLACE_LABEL, url: geoportalMapviewUrl(latlng.lat, latlng.lng) });

/**
 * Overlay layers that answer a click on the map in the point panel, in the order of the layer
 * panel. `controllerKey` is the layer's key in the layer controllers, `urlId` its `layers=`
 * token and the `warstwa` of /api/warstwy/punkt. A layer with `linkOnly` has no data to read at
 * a point; its section links to the source service, with an optional `help`. `placeLink` is the
 * source service at the point, shown also when the lookup fails.
 */
export const LAYER_POINT_SOURCES = [
    { type: 'planMpzp', controllerKey: 'mpzp', urlId: 'mpzp',
        label: 'Plan miejscowy (Geoportal)', placeLink: geoportalPlace },
    { type: 'planMpzpRu', controllerKey: 'mpzpRu', urlId: 'mpzp-ru',
        label: 'Plan miejscowy (Rejestr urbanistyczny)', placeLink: geoportalPlace },
    { type: 'planPog', controllerKey: 'pog', urlId: 'pog', label: 'Plan ogólny gminy',
        placeLink: () => ({ label: 'Plany ogólne w Rejestrze urbanistycznym', url: 'https://rejestr-urbanistyczny.gov.pl/published/pog' }) },
    { type: 'planStudium', controllerKey: 'studium', urlId: 'studium',
        label: 'Studium uwarunkowań gminy', placeLink: geoportalPlace },
    { type: 'portPolska', controllerKey: 'portPolska', urlId: 'port-polska', label: 'Rezerwacje terenu Port Polska', placeLink: geoportalPlace },
    { type: 'flood', controllerKey: 'powodz', urlId: 'powodz', label: 'Zagrożenie powodziowe',
        placeLink: () => ({ label: 'Otwórz mapy zagrożenia powodziowego', url: 'https://wody.isok.gov.pl/imap_kzgw/?gpmap=gpMZP' }) },
    { type: 'utilities', controllerKey: 'gesut', label: 'Sieci uzbrojenia terenu', linkOnly: true, placeLink: geoportalPlace,
        message: 'Usługa uzbrojenia terenu udostępnia tylko rysunek przewodów, bez ich opisu. Rodzaj sieci rozpoznasz po kolorze linii; objaśnienie kolorów pokazuje legenda tej usługi w Geoportalu.',
        source: 'Źródło: Krajowa Integracja Uzbrojenia Terenu, Główny Urząd Geodezji i Kartografii' },
    { type: 'internet', controllerKey: 'sidusis', label: 'Dostępność internetu', linkOnly: true,
        placeLink: (latlng, zoom) => ({ label: 'Sprawdź to miejsce na internet.gov.pl', url: buildSidusisPlaceUrl(latlng, zoom) }),
        message: 'Status internetu stacjonarnego pod adresem pokazuje mapa internet.gov.pl po kliknięciu punktu adresowego budynku.',
        help: 'Skąd pochodzi status internetu pod adresem\n'
            + 'Sposób wyznaczenia: operatorzy telekomunikacyjni zgłaszają do systemu Ministerstwa Cyfryzacji adresy, pod którymi mogą świadczyć usługi internetu stacjonarnego, i miejsca planowanej budowy sieci. '
            + 'Na tej podstawie mapa internet.gov.pl pokazuje status punktu adresowego, na przykład „w zasięgu”, „zasięg teoretyczny” albo „brak zasięgu”.\n'
            + 'Uwaga: nazwy operatorów, rodzaje łączy i maksymalne prędkości pobierania serwis podaje w szczegółach adresu, zwykle po bezpłatnym zalogowaniu przez login.gov.pl. '
            + 'Możliwość podłączenia i cenę potwierdza operator.\n'
            + guideLine('jak-korzystac-z-metroskopu#internet-i-media', 'jak sprawdzić dostęp do internetu'),
        source: 'Źródło: System Informacyjny o Dostępie do Usług Stacjonarnego Internetu Szerokopasmowego (SIDUSIS), Ministerstwo Cyfryzacji' },
];

export const LAYER_POINT_TYPES = LAYER_POINT_SOURCES.map((definition) => definition.type);

/**
 * Point query sources (point-query-session.js) of the switched-on overlay layers. Every source
 * resolves to exactly one hit, so each layer gets its section also when its source has nothing
 * at the point; an unavailable external service fails the source, whose retry asks again.
 */
export function layerPointQuerySources(config) {
    const { latlng, zoom, baseUrl, getController } = config;
    return LAYER_POINT_SOURCES.filter((definition) => {
        const controller = getController(definition.controllerKey);
        return controller && controller.isDesired();
    }).map((definition) => {
        const placeLink = definition.placeLink(latlng, zoom);
        const source = { key: 'layer:' + definition.type, types: [definition.type], label: definition.label, errorLink: placeLink };
        source.request = definition.linkOnly
            ? () => Promise.resolve([{ type: definition.type, id: definition.type, data: {
                layer: definition.controllerKey, status: 'external', items: [], message: definition.message,
                help: definition.help || null, source: definition.source, links: placeLink && placeLink.url ? [placeLink] : [],
            } }])
            : (signal) => fetch(baseUrl + 'api/warstwy/punkt?' + qstr({ warstwa: definition.urlId, lat: latlng.lat, lng: latlng.lng }), { signal })
                .then((response) => {
                    if (!response.ok) throw new Error('HTTP ' + response.status);
                    return response.json();
                })
                .then((data) => {
                    if (!data || data.status === 'unavailable') throw new Error('Źródło warstwy nie odpowiedziało.');
                    return [{ type: definition.type, id: definition.type, data }];
                });
        return source;
    });
}
